/**
 * GraphQL Impersonation Hooks
 * Feature: 075-link-based-impersonation
 *
 * Hooks for admin impersonation link operations:
 * - Create impersonation link (admin only)
 * - Exchange impersonation token for JWT (public)
 */

import { useMutation, gql } from '@apollo/client';
import { apolloClient } from '../../graphql/client';
import logger from '../../utils/logger';

// GraphQL Mutations
const CREATE_IMPERSONATION_LINK_MUTATION = gql`
  mutation AdminCreateImpersonationLink($input: AdminUserIdInput!) {
    adminCreateImpersonationLink(input: $input) {
      url
      pin
      expiresAt
      targetUsername
    }
  }
`;

const EXCHANGE_IMPERSONATION_TOKEN_MUTATION = gql`
  mutation ExchangeImpersonationToken($input: ExchangeImpersonationTokenInput!) {
    exchangeImpersonationToken(input: $input) {
      user {
        id
        globalId
        email
        username
        slug
        displayName
        createdAt
        updatedAt
      }
      token
    }
  }
`;

// Types
interface User {
  id: string;
  globalId: string;
  email: string;
  username: string;
  slug: string;
  displayName: string;
  createdAt: string;
  updatedAt: string;
}

interface ImpersonationLinkPayload {
  url: string;
  pin: string;
  expiresAt: string;
  targetUsername: string;
}

interface ExchangePayload {
  token: string;
  user: User;
}

/**
 * Hook for creating an impersonation link (admin only)
 *
 * Returns a URL with a nonce and a separate PIN.
 * The admin shows both to the user who needs to enter the PIN
 * on the landing page to complete authentication.
 */
export function useCreateImpersonationLinkGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { adminCreateImpersonationLink: ImpersonationLinkPayload },
    { input: { id: string } }
  >(CREATE_IMPERSONATION_LINK_MUTATION);

  const createLink = async (userId: string) => {
    logger.info('Admin', 'Creating impersonation link via GraphQL', { targetUserId: userId });

    const result = await mutate({ variables: { input: { id: userId } } });

    if (result.data?.adminCreateImpersonationLink) {
      logger.info('Admin', 'Impersonation link created', {
        targetUsername: result.data.adminCreateImpersonationLink.targetUsername,
      });
    }

    return result.data?.adminCreateImpersonationLink;
  };

  return {
    createLink,
    data: data?.adminCreateImpersonationLink,
    loading,
    error,
  };
}

/**
 * Hook for exchanging an impersonation token for a JWT
 *
 * Called from the landing page after the user enters the PIN.
 * On success, stores the token and resets the Apollo cache.
 */
export function useExchangeImpersonationTokenGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { exchangeImpersonationToken: ExchangePayload },
    { input: { nonce: string; pin: string } }
  >(EXCHANGE_IMPERSONATION_TOKEN_MUTATION);

  const exchangeToken = async (nonce: string, pin: string) => {
    logger.info('Auth', 'Exchanging impersonation token via GraphQL');

    const result = await mutate({ variables: { input: { nonce, pin } } });

    if (result.data?.exchangeImpersonationToken) {
      const { token, user } = result.data.exchangeImpersonationToken;

      // Store token and user in localStorage (matching existing auth pattern)
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Reset Apollo cache on login (matches useLoginGraphQL pattern)
      await apolloClient.resetStore();

      logger.info('Auth', 'Impersonation login successful', { userId: user.id });
    }

    return result.data?.exchangeImpersonationToken;
  };

  return {
    exchangeToken,
    data: data?.exchangeImpersonationToken,
    loading,
    error,
  };
}
